<template>
  <div class="pos-layout min-h-screen bg-gray-100">
    <!-- Header -->
    <header class="bg-blue-600 text-white shadow-lg">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Logo and Store Info -->
          <div class="flex items-center space-x-4">
            <router-link to="/pos/dashboard" class="flex items-center space-x-2">
              <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <span class="text-blue-600 font-bold text-xl">P</span>
              </div>
              <div>
                <span class="font-bold text-lg">POS System</span>
                <span class="block text-xs text-blue-200">{{ currentDate }}</span>
              </div>
            </router-link>
          </div>

          <!-- Navigation -->
          <nav class="hidden md:flex items-center space-x-6">
            <router-link 
              to="/pos/dashboard" 
              class="hover:text-blue-200 transition-colors"
              active-class="text-yellow-400"
            >
              Dashboard
            </router-link>
            <router-link 
              to="/pos/new-order" 
              class="hover:text-blue-200 transition-colors"
              active-class="text-yellow-400"
            >
              Nuovo Ordine
            </router-link>
            <router-link 
              to="/pos/today" 
              class="hover:text-blue-200 transition-colors"
              active-class="text-yellow-400"
            >
              Ordini Oggi
            </router-link>
            <router-link 
              to="/pos/reorder" 
              class="hover:text-blue-200 transition-colors"
              active-class="text-yellow-400"
            >
              Riordino Veloce
            </router-link>
          </nav>

          <!-- User Actions -->
          <div class="flex items-center space-x-4">
            <!-- Cash Register Status -->
            <div class="hidden sm:flex items-center space-x-2 bg-blue-700 px-3 py-1 rounded-lg">
              <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm">Cassa Aperta</span>
            </div>

            <!-- User Menu -->
            <div class="relative">
              <button 
                @click="userMenuOpen = !userMenuOpen"
                class="flex items-center space-x-2 hover:text-blue-200 transition-colors"
              >
                <div class="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
                  <span class="font-medium">{{ userInitials }}</span>
                </div>
                <span class="hidden sm:block">{{ userName }}</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <!-- Dropdown Menu -->
              <div 
                v-if="userMenuOpen"
                class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-50"
              >
                <router-link 
                  to="/admin/dashboard"
                  @click="userMenuOpen = false"
                  class="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                >
                  Admin Panel
                </router-link>
                <hr class="my-1">
                <button 
                  @click="openCashDrawer"
                  class="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                >
                  Apri Cassetto
                </button>
                <button 
                  @click="printDailyReport"
                  class="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                >
                  Report Giornaliero
                </button>
                <hr class="my-1">
                <button 
                  @click="logout"
                  class="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                >
                  Logout
                </button>
              </div>
            </div>

            <!-- Mobile menu button -->
            <button 
              @click="mobileMenuOpen = !mobileMenuOpen"
              class="md:hidden p-2 hover:bg-blue-700 rounded-lg"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  v-if="!mobileMenuOpen"
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M4 6h16M4 12h16M4 18h16"
                />
                <path 
                  v-else
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Mobile Navigation -->
        <div 
          v-if="mobileMenuOpen"
          class="md:hidden border-t border-blue-500 py-4"
        >
          <nav class="flex flex-col space-y-2">
            <router-link 
              to="/pos/dashboard" 
              @click="mobileMenuOpen = false"
              class="px-2 py-1 hover:bg-blue-700 rounded"
              active-class="bg-blue-700"
            >
              Dashboard
            </router-link>
            <router-link 
              to="/pos/new-order" 
              @click="mobileMenuOpen = false"
              class="px-2 py-1 hover:bg-blue-700 rounded"
              active-class="bg-blue-700"
            >
              Nuovo Ordine
            </router-link>
            <router-link 
              to="/pos/today" 
              @click="mobileMenuOpen = false"
              class="px-2 py-1 hover:bg-blue-700 rounded"
              active-class="bg-blue-700"
            >
              Ordini Oggi
            </router-link>
            <router-link 
              to="/pos/reorder" 
              @click="mobileMenuOpen = false"
              class="px-2 py-1 hover:bg-blue-700 rounded"
              active-class="bg-blue-700"
            >
              Riordino Veloce
            </router-link>
          </nav>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-4 py-6">
      <slot />
    </main>

    <!-- Quick Action Bar -->
    <div class="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between py-3">
          <!-- Status Info -->
          <div class="flex items-center space-x-6">
            <div class="text-sm">
              <span class="text-gray-500">Ordini Oggi:</span>
              <span class="font-bold text-gray-900 ml-1">{{ todayOrdersCount }}</span>
            </div>
            <div class="text-sm">
              <span class="text-gray-500">Totale:</span>
              <span class="font-bold text-gray-900 ml-1">€{{ todayTotal }}</span>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="flex items-center space-x-2">
            <button 
              @click="quickNewOrder"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Nuovo Ordine
            </button>
            <button 
              @click="openCalculator"
              class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrdersStore } from '@/stores/orders'
import { useAppStore } from '@/stores/app'

const router = useRouter()
const authStore = useAuthStore()
const ordersStore = useOrdersStore()
const appStore = useAppStore()

const mobileMenuOpen = ref(false)
const userMenuOpen = ref(false)
const currentDate = ref('')

const userName = computed(() => authStore.user?.name || 'Operatore')
const userInitials = computed(() => {
  const name = authStore.user?.name || 'OP'
  return name.split(' ').map(n => n[0]).join('').toUpperCase()
})

const todayOrdersCount = computed(() => ordersStore.todayOrders?.length || 0)
const todayTotal = computed(() => {
  const total = ordersStore.todayOrders?.reduce((sum, order) => sum + order.total, 0) || 0
  return total.toFixed(2)
})

const updateDate = () => {
  const now = new Date()
  currentDate.value = now.toLocaleDateString('it-IT', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

const openCashDrawer = () => {
  // Implement cash drawer opening logic
  appStore.showSuccess('Cassetto aperto')
  userMenuOpen.value = false
}

const printDailyReport = () => {
  // Implement daily report printing
  appStore.showSuccess('Report in stampa...')
  userMenuOpen.value = false
}

const quickNewOrder = () => {
  router.push('/pos/new-order')
}

const openCalculator = () => {
  // Implement calculator modal
  appStore.showInfo('Calcolatrice non ancora implementata')
}

const logout = async () => {
  await authStore.logout()
  router.push('/auth/login')
}

let dateInterval

onMounted(() => {
  updateDate()
  // Update date every minute
  dateInterval = setInterval(updateDate, 60000)
  
  // Load today's orders
  ordersStore.fetchTodayOrders()
  
  // Close dropdowns on click outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.relative')) {
      userMenuOpen.value = false
    }
  })
})

onUnmounted(() => {
  if (dateInterval) {
    clearInterval(dateInterval)
  }
})
</script>

<style scoped>
.pos-layout {
  padding-bottom: 80px; /* Space for fixed bottom bar */
}

.router-link-active {
  @apply text-yellow-400 font-medium;
}
</style>
